import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'Emergency Cleanup', path: '/emergency-cleanup' },
  { label: 'Property Restoration', path: '/property-restoration' },
  { label: 'Pipe Leak & Drainage', path: '/pipe-leak-drainage' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://static.readdy.ai/image/950b0b3f1f9eed6b6b77e430ea7654eb/7148e27a836d270da7b9e8979d76c8d6.jpeg"
            alt="Mitigation Restoration Services Logo"
            className="h-12 w-12 rounded-lg object-cover"
          />
          <div className="hidden sm:block">
            <p className={`font-bold text-base leading-tight whitespace-nowrap ${scrolled ? 'text-[#1E3A5F]' : 'text-white'}`}>
              Mitigation Restoration
            </p>
            <p className={`text-xs leading-tight ${scrolled ? 'text-gray-500' : 'text-white/80'}`}>
              Services
            </p>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === link.path
                  ? scrolled
                    ? 'text-[#DC2626] font-semibold'
                    : 'text-white font-semibold underline underline-offset-4'
                  : scrolled
                  ? 'text-gray-700 hover:text-[#DC2626]'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA Buttons */}
        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/service-request"
            className="inline-flex items-center gap-2 bg-[#1E3A5F] text-white px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#162d4a] transition-colors"
          >
            <i className="ri-file-list-3-fill text-base"></i>
            Request Service
          </Link>
          <a
            href="tel:+18005551234"
            className="inline-flex items-center gap-2 bg-[#DC2626] text-white px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-red-700 transition-colors"
          >
            <i className="ri-phone-fill text-base"></i>
            24/7 Emergency
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${scrolled ? 'text-[#1E3A5F]' : 'text-white'}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`block px-6 py-3.5 text-sm font-medium border-b border-gray-50 cursor-pointer ${
                location.pathname === link.path ? 'text-[#DC2626] bg-red-50' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="px-6 py-4 flex flex-col gap-3">
            <Link
              to="/service-request"
              className="flex items-center justify-center gap-2 bg-[#1E3A5F] text-white px-5 py-3 rounded-full text-sm font-semibold cursor-pointer"
            >
              <i className="ri-file-list-3-fill"></i>
              Request Service
            </Link>
            <a
              href="tel:+18005551234"
              className="flex items-center justify-center gap-2 bg-[#DC2626] text-white px-5 py-3 rounded-full text-sm font-semibold cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              24/7 Emergency Call
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
